"use client";
import Link from "next/link";
import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import AsoebiModal from "@src/components/AsoebiModal";
import Dialog from "src/components/Dialog";

interface Props {
  eventUuid: string | undefined;
}

const AsoEbi = ({ eventUuid }: Props) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const buttonVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
      y: 20,
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "backOut",
        delay: 0.8,
      },
    },
    hover: {
      scale: 1.05,
      boxShadow: "0 10px 25px rgba(255, 255, 255, 0.2)",
      transition: {
        duration: 0.2,
        ease: "easeInOut",
      },
    },
    tap: {
      scale: 0.98,
      transition: {
        duration: 0.1,
      },
    },
  };

  const backgroundVariants = {
    hidden: {
      scale: 1,
      opacity: 0,
    },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 1.2,
        ease: "easeOut",
      },
    },
  };

  const [openAsoebi, setOpenAsoebi] = useState(false);

  return (
    <motion.div
      className="h-[60vh] bg-[#E2D4D2] flex flex-col justify-center py-1 border-t-4 border-b-2 border-black"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <div className="border-t-2 border-b-4 border-black h-[58vh] py-6 flex flex-col justify-center items-center">
        <motion.div
          ref={ref}
          className="h-full w-full flex justify-center items-center relative z-10 px-4 md:px-8 overflow-hidden"
          style={{
            backgroundImage: `url('/images/t8-pattern.png')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
          variants={backgroundVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          whileHover={{
            // scale: 1.02,
            transition: { duration: 0.3 },
          }}
        >
          {/* Animated overlay for depth */}
          <motion.div
            className="absolute inset-0 bg-black/50 z-0"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
          />

          {/* Content above overlay */}
          <motion.div
            className="relative z-10 flex flex-col justify-center items-center w-full lg:w-3/4"
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            <motion.h3
              className="font-neue font-light text-center text-2xl mb-4 lg:text-6xl text-[#FFFFFF]"
              variants={itemVariants}
              whileHover={{
                // scale: 1.02,
                textShadow: "0 0 20px rgba(255, 255, 255, 0.5)",
                transition: { duration: 0.2 },
              }}
            >
              Our Asoebi
            </motion.h3>

            <motion.h5
              className="font-ebGaramond font-normal text-center text-lg lg:text-2xl text-[#FFFFFF]"
              variants={itemVariants}
            >
              The beautiful attires for our wedding.
            </motion.h5>

            <Dialog
              trigger={
                <motion.div
                  className="flex flex-col md:flex-row mt-8 gap-8 justify-center items-center"
                  variants={itemVariants}
                >
                  <motion.button
                    className="px-4 py-2 rounded-xl transition duration-300 border border-white text-white text-base font-ebGaramond uppercase hover:bg-white hover:text-[#302d2c] relative overflow-hidden"
                    type="button"
                    variants={buttonVariants}
                    initial="hidden"
                    animate={isInView ? "visible" : "hidden"}
                    whileHover="hover"
                    whileTap="tap"
                  >
                    {/* Ripple effect background */}
                    <motion.div
                      className="absolute inset-0 bg-white rounded"
                      initial={{ scale: 0, opacity: 0 }}
                      whileHover={{
                        scale: 1,
                        opacity: 0.1,
                        transition: { duration: 0.3 },
                      }}
                    />

                    {/* Animated border glow */}
                    <motion.div
                      className="absolute inset-0 border border-white rounded"
                      animate={{
                        boxShadow: [
                          "0 0 0px rgba(255, 255, 255, 0.5)",
                          "0 0 10px rgba(255, 255, 255, 0.8)",
                          "0 0 0px rgba(255, 255, 255, 0.5)",
                        ],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Number.POSITIVE_INFINITY,
                        ease: "easeInOut",
                      }}
                    />

                    <p className="relative z-10">Get Your Asoebi</p>
                  </motion.button>
                </motion.div>
              }
              open={openAsoebi}
              onOpenChange={setOpenAsoebi}
            >
              <AsoebiModal eventUuid={eventUuid} setOpen={setOpenAsoebi} />
            </Dialog>
          </motion.div>

          {/* Floating decorative elements */}
          <motion.div
            className="absolute top-10 left-10 w-2 h-2 bg-white/30 rounded-full"
            animate={{
              y: [0, -10, 0],
              opacity: [0.3, 0.7, 0.3],
            }}
            transition={{
              duration: 3,
              repeat: Number.POSITIVE_INFINITY,
              ease: "easeInOut",
            }}
          />

          <motion.div
            className="absolute bottom-20 right-20 w-3 h-3 bg-white/20 rounded-full"
            animate={{
              y: [0, -15, 0],
              x: [0, 5, 0],
              opacity: [0.2, 0.6, 0.2],
            }}
            transition={{
              duration: 4,
              repeat: Number.POSITIVE_INFINITY,
              ease: "easeInOut",
              delay: 1,
            }}
          />
        </motion.div>
      </div>
    </motion.div>
  );
};

export default AsoEbi;
